import { DefaultMap, dontAwait } from "@glideapps/ts-necessities";
import sortBy from "lodash/sortBy";
import { v4 as uuid } from "uuid";
import { isKeyRefObject } from "../common/components/data";
import type { CurrentLocationListener } from "../common/components/functions-glide";
import { listenToCurrentLocation, stopListeningToCurrentLocation } from "../common/components/functions-glide";
import type { GroundValue, LoadingValue, PrimitiveValue, Row, Table } from "../common/computation-model/data";
import {
    asMaybeNumber,
    asPrimitive,
    asTable,
    containerAsArray,
    deconstructTableColumnPath,
    follow,
    forEachItem,
    getArrayItem,
    getRowColumn,
    getSymbolicRepresentationForGroundValue,
    isLoadingValue,
    isPrimitive,
    isPrimitiveValue,
    isTruthy,
    loadedDefinedMap,
    makeLoadingValue,
    setThunkColumn,
    tableForEach,
} from "../common/computation-model/data";
import type { KeyPath, Path, RelativePath, RootPath } from "../common/computation-model/paths";
import {
    getSymbolicRepresentationForPath,
    isRootPath,
    isTopLevelPath,
    makeKeyPath,
    makeRootPath,
} from "../common/computation-model/paths";
import type {
    AsyncComputation,
    Computation,
    Dirt,
    DirtyState,
    Handler,
    IncomingSlot,
    MapperState,
    Namespace,
    ProcessDirtProcessor,
    RootPathResolver,
    TableAggregateComputation,
    TableAggregateDataProvider,
} from "../common/computation-model/types";
import type { LatLng } from "../common/Database";
import type { BaseRowIndex } from "../common/firebase-function-types";
import { isBaseRowIndex } from "../common/firebase-function-types";
import { rowIndexColumnName } from "../common/google-sheets";
import { makeRowID } from "../common/make-row-id";
import type { Ref } from "../common/ref";
import { GlideDateTime } from "../date-time/glide-date-time";
import { areSetsOverlapping, assert, assertNever, checkString, defined, isArray } from "../support";
import type { Condition, ConditionValuePath } from "./conditions";
import { computeCondition, getPathsForCondition, getSymbolicRepresentationForCondition } from "./conditions";
import { makeDirtyState } from "./dirty-state";
import { dirtyFromTheStart } from "./globals";
import { DateTimeAsyncParser } from "./parse-date-time";

// TODO:
//
// * Some computations can do relation-dirtying better than we do now with the
//   combination nodes.  For example, extract minimum row knows which row it
//   needs to pay attention to.

// This is only for internal use in the computation model.
export function getValueAt(ns: RootPathResolver, context: GroundValue, p: Path): GroundValue {
    if (isRootPath(p)) {
        return ns.get(p, true);
    } else {
        return follow(context, p);
    }
}

export function makeGetValueAt(ns: RootPathResolver, p: Path): (context: GroundValue) => GroundValue {
    if (isRootPath(p)) {
        const v = ns.get(p, true);
        return () => v;
    } else {
        return context => follow(context, p);
    }
}

// This the same as `getValueAt`, except it can be called anywhere because it
// doesn't assert that dependencies are not dirty.
export function getValueAtPath(ns: RootPathResolver, context: GroundValue, p: Path): GroundValue {
    if (isRootPath(p)) {
        return ns.get(p);
    } else {
        return follow(context, p);
    }
}

// Dirt:
// * never pushes dirt
export class ConstantValueHandler implements Handler {
    constructor(private readonly _value: GroundValue) {}

    public getSlots(): readonly IncomingSlot[] {
        return [];
    }

    public recompute(): GroundValue {
        return this._value;
    }

    public setDirty(): void {
        return;
    }

    public get isDirty(): boolean {
        return false;
    }

    public get symbolicRepresentation(): string {
        return `constant ${getSymbolicRepresentationForGroundValue(this._value)}`;
    }
}

// Dirt:
// * pushes dirt whenever a value in its column is added or changed
export class DataColumnHandler implements Handler {
    private readonly _slot: IncomingSlot;
    private readonly _dirtyState = makeDirtyState(true);
    private readonly _values = new DefaultMap<string, PrimitiveValue>(() => this._defaultValue);

    constructor(
        tablePath: RootPath,
        private readonly _columnName: string,
        private readonly _defaultValue: PrimitiveValue
    ) {
        this._slot = { sourcePath: tablePath, process: d => this._dirtyState.add(d) };
    }

    public getSlots(): readonly IncomingSlot[] {
        return [this._slot];
    }

    public recompute(ns: Namespace): GroundValue {
        const tableValue = ns.get(this._slot.sourcePath);
        const { dirtyRowIDs } = this._dirtyState;

        if (dirtyRowIDs !== false && !isLoadingValue(tableValue)) {
            const table = asTable(tableValue);
            const outgoing = makeDirtyState(true);

            const processRow = (r: Row) => {
                const existing = getRowColumn(r, this._columnName);
                const v = this._values.get(r.$rowID);
                if (existing === v) return;
                r[this._columnName] = v;
                outgoing.addRowAndColumn(r.$rowID, this._columnName);
            };

            if (dirtyRowIDs === true) {
                tableForEach(table, true, processRow);
            } else {
                for (const rowID of dirtyRowIDs) {
                    const r = table.get(rowID);
                    if (r === undefined) continue;
                    processRow(r);
                }
            }

            outgoing.pushAndClear(ns, this);
        }

        this._dirtyState.clear();

        return tableValue;
    }

    public setValueInRow(ns: Namespace, rowID: string, value: PrimitiveValue): void {
        const current = this._values.get(rowID);
        if (value === current) return;

        this._values.set(rowID, value);

        this._dirtyState.addRowAndColumn(rowID, this._columnName);
        ns.handlerWasDirtied(this);
    }

    public setDirty(): void {
        this._dirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._dirtyState.isDirty;
    }

    public get symbolicRepresentation(): string {
        return `${this._columnName} = data default: ${getSymbolicRepresentationForGroundValue(this._defaultValue)}`;
    }
}

interface Group {
    readonly rowID: string;
}

// Not used by Glide at the moment
//
// Dirt: (FIXME: implement this spec)
// * pushes dirt when groups (rows in the group table) are added/removed
// * pushed dirt when group membership changes
// * does not push dirt for changes in grouped rows, other than for the above
//   two cases.
//
// FIXME: Use thunks
export class GroupByHandler implements Handler {
    private readonly _slot: IncomingSlot;
    private readonly _columnPath: KeyPath;
    private readonly _groups = new DefaultMap<PrimitiveValue, Group>(() => ({
        rowID: makeRowID(),
    }));
    private _value: Table | LoadingValue = new Map();
    private readonly _dirtyState;

    constructor(tableColumnPath: RootPath) {
        const { tablePath, keyPath } = deconstructTableColumnPath(tableColumnPath);
        this._slot = { sourcePath: tablePath, process: this.processDirt };
        this._columnPath = keyPath;
        this._dirtyState = makeDirtyState(keyPath);
    }

    public getSlots(): readonly IncomingSlot[] {
        return [this._slot];
    }

    private readonly processDirt = (d: Dirt): boolean => {
        return this._dirtyState.add(d);
    };

    public recompute(ns: Namespace): GroundValue {
        if (!this.isDirty) {
            return this._value;
        }

        const groups = new DefaultMap<PrimitiveValue, Table>(() => new Map());
        const tableValue = ns.get(this._slot.sourcePath, true);
        if (!isLoadingValue(tableValue)) {
            const table = asTable(tableValue);
            tableForEach(table, false, row => {
                const key = follow(row, this._columnPath);
                if (key === undefined || isLoadingValue(key)) return;
                groups.get(asPrimitive(key)).set(row.$rowID, row);
            });
            this._value = new Map(
                Array.from(groups.entries()).map(([k, r]) => {
                    const rowID = this._groups.get(k).rowID;
                    const row: Row = {
                        $rowID: rowID,
                        $isVisible: true,
                        group: k,
                        items: r,
                    };
                    return [rowID, row];
                })
            );
        } else {
            this._value = tableValue;
        }

        ns.pushDirt(this, { kind: "table", columns: true });

        this._dirtyState.clear();

        return this._value;
    }

    public setDirty(): void {
        this._dirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._dirtyState.isDirty;
    }

    public get symbolicRepresentation(): string {
        return `group ${getSymbolicRepresentationForPath(this._slot.sourcePath)} by: ${this._columnPath}`;
    }
}

// Makes slots for all the root paths in `paths`.
function makeSlotsForPaths(
    paths: readonly Path[],
    panicOnNonRootPaths: boolean,
    processDirt: (d: Dirt, ps: readonly RootPath[]) => boolean
): readonly IncomingSlot[] {
    const keys = new DefaultMap<string, RootPath[]>(() => []);
    for (const p of paths) {
        if (!isRootPath(p)) {
            assert(!panicOnNonRootPaths);
            continue;
        }
        keys.get(p.rest.key).push(p);
    }

    return Array.from(keys.entries()).map(([k, ps]) => ({
        sourcePath: makeRootPath(k),
        process: d => processDirt(d, ps),
    }));
}

// This class has some boilerplate for handlers that process one main
// table a bunch of ancillary data.
abstract class TableAndPathsHandler implements Handler {
    private readonly _slots: readonly IncomingSlot[];

    // The callback for `tablePath` is `processTableDirt`.  All the paths
    // in `paths` use `processOtherDirt`.
    constructor(tablePath: RootPath, paths: readonly Path[]) {
        const tableSlot: IncomingSlot = { sourcePath: tablePath, process: d => this.processTableDirt(d) };
        const otherSlots = makeSlotsForPaths(paths, false, (d, ps) => this.processOtherDirt(d, ps));
        this._slots = [tableSlot, ...otherSlots];
    }

    // The main table.
    protected getTable(ns: Namespace): Table | LoadingValue {
        const tablePath = defined(this._slots[0]).sourcePath;
        const tableValue = ns.get(tablePath, true);
        if (isLoadingValue(tableValue)) return tableValue;
        return asTable(tableValue);
    }

    public getSlots(): readonly IncomingSlot[] {
        return this._slots;
    }

    protected abstract processTableDirt(d: Dirt): boolean;
    protected abstract processOtherDirt(d: Dirt, paths: readonly RootPath[]): boolean;

    public abstract recompute(ns: Namespace): GroundValue;

    public abstract setDirty(): void;

    public abstract get isDirty(): boolean;
    public abstract get symbolicRepresentation(): string;
}

enum RecomputeMembershipLevel {
    None,
    DirtyRows,
    All,
}

// Handler which produces a filtered version of a given table.
//
// Dirt:
// * pushes dirt when a row becomes included/excluded
// * pushes dirt from changes in included rows
abstract class FilteringHandler extends TableAndPathsHandler {
    // Subclasses must check this in `needRecomputeMembership`, but must not
    // modify it.
    protected readonly tableDirtyState = makeDirtyState(true);

    // Indexed by row ID.  `undefined` means loading.
    protected rowsIncluded: Map<string, Row> | undefined;

    protected abstract isRowIncluded(ns: Namespace, row: Row): boolean;
    protected abstract get needRecomputeMembership(): RecomputeMembershipLevel;

    constructor(
        tablePath: RootPath,
        paths: readonly Path[],
        private readonly _sortKey: RelativePath | undefined,
        private readonly _fallbackRowID: string | undefined
    ) {
        super(tablePath, paths);
    }

    protected processTableDirt(d: Dirt): boolean {
        return this.tableDirtyState.add(d);
    }

    private get(): GroundValue | LoadingValue {
        assert(!this.tableDirtyState.isDirty);
        return this.rowsIncluded;
    }

    public recompute(ns: Namespace): GroundValue {
        const outgoing = makeDirtyState(true);

        const level = this.needRecomputeMembership;

        if (level !== RecomputeMembershipLevel.None) {
            const table = this.getTable(ns);

            if (isLoadingValue(table)) {
                this.rowsIncluded = undefined;
            } else if (level === RecomputeMembershipLevel.All) {
                const oldRowsIncluded = this.rowsIncluded ?? new Map();
                this.rowsIncluded = new Map();

                // We have to dirty all rows that were deleted.
                for (const rowID of oldRowsIncluded.keys()) {
                    if (!table.has(rowID)) {
                        outgoing.addRowAndColumn(rowID, true);
                    }
                }

                tableForEach(table, false, row => {
                    let isIncluded = oldRowsIncluded.has(row.$rowID);
                    const newIsIncluded = this.isRowIncluded(ns, row);
                    if (isIncluded !== newIsIncluded) {
                        outgoing.addRowAndColumn(row.$rowID, true);
                        isIncluded = newIsIncluded;
                    }
                    if (isIncluded) {
                        defined(this.rowsIncluded).set(row.$rowID, row);
                    }
                });
            } else if (level === RecomputeMembershipLevel.DirtyRows) {
                const oldRowsIncluded = this.rowsIncluded ?? new Map();

                const { dirtyRowIDs } = this.tableDirtyState;
                assert(typeof dirtyRowIDs !== "boolean");

                const { dirtyColumns } = this.tableDirtyState;
                assert(dirtyColumns !== false);

                for (const rowID of dirtyRowIDs) {
                    const row = table.get(rowID);
                    if (row?.$isVisible === false) continue;
                    const oldIsIncluded = oldRowsIncluded.has(rowID);
                    const newIsIncluded = row !== undefined && this.isRowIncluded(ns, row);

                    if (oldIsIncluded === newIsIncluded) {
                        outgoing.add({ kind: "row", rowID, columns: dirtyColumns });
                    } else {
                        if (newIsIncluded) {
                            oldRowsIncluded.set(rowID, defined(row));
                        } else {
                            oldRowsIncluded.delete(rowID);
                        }

                        outgoing.addRowAndColumn(rowID, true);
                    }
                }

                this.rowsIncluded = sortRowsIntoTable(Array.from(oldRowsIncluded.values()), this._sortKey);
            } else {
                assertNever(level);
            }

            if (
                !isLoadingValue(table) &&
                (this.rowsIncluded === undefined || this.rowsIncluded.size === 0) &&
                this._fallbackRowID !== undefined
            ) {
                const row = table.get(this._fallbackRowID);
                if (row !== undefined) {
                    if (this.rowsIncluded === undefined) {
                        this.rowsIncluded = new Map();
                    }
                    this.rowsIncluded.set(this._fallbackRowID, row);
                    outgoing.addRowAndColumn(this._fallbackRowID, true);
                }
            }
        }

        outgoing.pushAndClear(ns, this);
        this.tableDirtyState.clear();

        return this.get();
    }

    public setDirty(): void {
        this.tableDirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this.needRecomputeMembership !== RecomputeMembershipLevel.None;
    }
}

export interface FilterPredicate {
    readonly symbolicRepresentation: string;

    // This includes global paths
    getFilteredPaths(): readonly Path[];
    getHostPaths(): readonly RelativePath[];
    isRowIncluded(ns: RootPathResolver, filteredRow: Row, hostRow: Row | undefined, setAllDirty: () => void): boolean;
}

// This is only used in tests at this point, not in Glide.
export class ColumnFilterPredicate implements FilterPredicate {
    constructor(private readonly _columnPath: RelativePath) {}

    public getFilteredPaths(): readonly Path[] {
        return [this._columnPath];
    }

    public getHostPaths(): readonly RelativePath[] {
        return [];
    }

    public isRowIncluded(_ns: RootPathResolver, row: Row): boolean {
        const v = follow(row, this._columnPath);
        return !isLoadingValue(v) && isTruthy(v);
    }

    public get symbolicRepresentation(): string {
        return `(column: ${getSymbolicRepresentationForPath(this._columnPath)})`;
    }
}

// FIXME: We're invoking this one for every row in some computations, which,
// given how `getValueAt` calls back to the namespace, is probably very
// expensive.
export function makePathOrGroundValueGetter(
    ns: RootPathResolver,
    context: GroundValue,
    hostRow: Row | undefined
): (pov: ConditionValuePath) => GroundValue {
    return (pov: ConditionValuePath) => {
        let v: GroundValue;
        if (pov.inHostRow) {
            assert(hostRow !== undefined && !isRootPath(pov.path));
            v = follow(hostRow, pov.path);
        } else {
            v = getValueAt(ns, context, pov.path);
        }
        return v;
    };
}

export class ConditionFilterPredicate extends DateTimeAsyncParser implements FilterPredicate {
    constructor(private readonly _condition: Condition<ConditionValuePath>) {
        super();
    }

    public getFilteredPaths(): readonly Path[] {
        return getPathsForCondition(this._condition, false);
    }

    public getHostPaths(): readonly RelativePath[] {
        return getPathsForCondition(this._condition, true);
    }

    public isRowIncluded(ns: RootPathResolver, row: Row, hostRow: Row | undefined, setAllDirty: () => void): boolean {
        const get = makePathOrGroundValueGetter(ns, row, hostRow);
        return computeCondition(this._condition, get, v => this.parseDateTime(v, setAllDirty)) === true;
    }

    public get symbolicRepresentation(): string {
        return getSymbolicRepresentationForCondition(this._condition);
    }
}

function makeSetAllDirty(ns: Namespace, handler: Handler): () => void {
    return () => {
        handler.setDirty();
        if (handler.isDirty) {
            // Some handlers can't be dirtied.
            ns.handlerWasDirtied(handler);
        }
    };
}

// Dirt: see superclass
export class FilterHandler extends FilteringHandler {
    public readonly symbolicRepresentation: string;

    constructor(
        tablePath: RootPath,
        private readonly _predicate: FilterPredicate,
        sortKey: RelativePath | undefined,
        fallbackRowID?: string
    ) {
        super(tablePath, _predicate.getFilteredPaths(), sortKey, fallbackRowID);
        assert(_predicate.getHostPaths().length === 0);

        let str = `filter ${tablePath} by: ${_predicate.symbolicRepresentation}`;
        if (sortKey !== undefined) {
            str = `${str} sortBy: ${getSymbolicRepresentationForPath(sortKey)}`;
        }
        this.symbolicRepresentation = str;
    }

    protected isRowIncluded(ns: Namespace, row: Row): boolean {
        assert(row.$isVisible);
        return this._predicate.isRowIncluded(ns, row, undefined, makeSetAllDirty(ns, this));
    }

    protected get needRecomputeMembership(): RecomputeMembershipLevel {
        if (this.tableDirtyState.dirtyRowIDs === true) {
            return RecomputeMembershipLevel.All;
        } else if (this.tableDirtyState.includesPaths(this._predicate.getFilteredPaths())) {
            return RecomputeMembershipLevel.DirtyRows;
        } else {
            return RecomputeMembershipLevel.None;
        }
    }

    protected processOtherDirt(_d: Dirt, _paths: readonly RootPath[]): boolean {
        return this.tableDirtyState.setAllDirty();
    }
}

// This is how the new computation model converts primitive values to strings
// when they are keys for relations.  ##convertingRelationKeys
function convertToLocaleAgnosticString(x: unknown): string | undefined {
    if (typeof x === "string") return x;
    if (typeof x === "number") return x.toString();
    if (typeof x === "boolean") return x.toString();
    if (x instanceof GlideDateTime) return x.asString();
    if (x === undefined) return "";
    return undefined;
}

// If `sourceKeyIsArray` then we return an empty array if it's `undefined`.
// Otherwise we return the empty string.
export function getRelationKeys(v: GroundValue, sourceKeyIsArray: boolean): readonly string[] {
    if (isArray(v)) {
        const a: string[] = [];
        for (let i = 0; i < v.length; i++) {
            const s = convertToLocaleAgnosticString(getArrayItem(v, i));
            if (s !== undefined) {
                a.push(s);
            }
        }
        return a;
    } else if (isKeyRefObject(v)) {
        const s = convertToLocaleAgnosticString(v.$value);
        if (s === undefined) return [];
        return [s];
    } else if (sourceKeyIsArray && v === undefined) {
        return [];
    } else {
        const s = convertToLocaleAgnosticString(v);
        if (s === undefined) return [];
        return [s];
    }
}

// Dirt: see superclass
abstract class MultiRelationHandler extends FilteringHandler {
    private _isSourceDirty = dirtyFromTheStart;

    constructor(
        private readonly _sourceKeyPath: RootPath,
        tablePath: RootPath,
        sortKey: RelativePath,
        private readonly _sourceKeyIsArray: boolean,
        private readonly _targetKeyIsArray: boolean
    ) {
        super(tablePath, [_sourceKeyPath], sortKey, undefined);
    }

    protected abstract getKeysForRow(ns: Namespace, row: Row): GroundValue;

    protected isRowIncluded(ns: Namespace, row: Row): boolean {
        assert(row.$isVisible);
        // FIXME: Only do this once, not for every row.  We might want to
        // make `FilteringHandler` generic and have a `prepare` function
        // which in this case would return this set, and then `isRowIncluded`
        // takes the result of `prepare` as an argument.
        const sourceKeys = new Set(getRelationKeys(ns.get(this._sourceKeyPath, true), this._sourceKeyIsArray));
        const keys = getRelationKeys(this.getKeysForRow(ns, row), this._targetKeyIsArray);
        return areSetsOverlapping(sourceKeys, keys);
    }

    protected get needRecomputeMembership(): RecomputeMembershipLevel {
        if (this._isSourceDirty || this.tableDirtyState.dirtyRowIDs === true) {
            return RecomputeMembershipLevel.All;
        } else {
            return RecomputeMembershipLevel.None;
        }
    }

    protected processOtherDirt(_d: Dirt, _paths: readonly RootPath[]): boolean {
        if (this._isSourceDirty) return false;

        // FIXME: This is too pessimistic.  At the very least we have to
        // filter by the source key path, which not be a top-level path.
        this._isSourceDirty = true;
        return true;
    }

    public recompute(ns: Namespace): GroundValue {
        const result = super.recompute(ns);
        this._isSourceDirty = false;
        return result;
    }
}

export class MultiRelationToColumnHandler extends MultiRelationHandler {
    private readonly _targetColumnPath: RelativePath;
    public readonly symbolicRepresentation: string;

    constructor(
        sourceKeyPath: RootPath,
        targetTableColumnPath: RootPath,
        sortKey: RelativePath,
        sourceKeyIsArray: boolean,
        targetKeyIsArray: boolean
    ) {
        super(
            sourceKeyPath,
            deconstructTableColumnPath(targetTableColumnPath).tablePath,
            sortKey,
            sourceKeyIsArray,
            targetKeyIsArray
        );
        this._targetColumnPath = deconstructTableColumnPath(targetTableColumnPath).keyPath;

        this.symbolicRepresentation = `multi-relation source: ${getSymbolicRepresentationForPath(
            sourceKeyPath
        )} target: ${getSymbolicRepresentationForPath(
            targetTableColumnPath
        )} sortBy: ${getSymbolicRepresentationForPath(sortKey)}`;
    }

    protected getKeysForRow(_ns: Namespace, row: Row): GroundValue {
        return follow(row, this._targetColumnPath);
    }

    protected get needRecomputeMembership(): RecomputeMembershipLevel {
        const fromSuper = super.needRecomputeMembership;
        if (fromSuper !== RecomputeMembershipLevel.All) {
            if (this.tableDirtyState.includesPaths([this._targetColumnPath])) {
                return RecomputeMembershipLevel.DirtyRows;
            }
        }
        return fromSuper;
    }
}

export class MultiRelationToGlobalHandler extends MultiRelationHandler {
    private _isTargetDirty = true;
    public readonly symbolicRepresentation: string;

    constructor(
        sourceKeyPath: RootPath,
        targetTablePath: RootPath,
        private readonly _targetPath: RootPath,
        sortKey: RelativePath,
        sourceKeyIsArray: boolean,
        targetKeyIsArray: boolean
    ) {
        super(sourceKeyPath, targetTablePath, sortKey, sourceKeyIsArray, targetKeyIsArray);

        this.symbolicRepresentation = `multi-relation source: ${getSymbolicRepresentationForPath(
            sourceKeyPath
        )} target: ${getSymbolicRepresentationForPath(_targetPath)} sortBy: ${getSymbolicRepresentationForPath(
            sortKey
        )}`;
    }

    public getSlots(): readonly IncomingSlot[] {
        return [
            ...super.getSlots(),
            {
                sourcePath: this._targetPath,
                process: this.processTargetDirt,
            },
        ];
    }

    private processTargetDirt = (): boolean => {
        if (this._isTargetDirty) return false;
        this._isTargetDirty = true;
        return true;
    };

    protected getKeysForRow(ns: Namespace, _row: Row): GroundValue {
        return ns.get(this._targetPath);
    }

    protected get needRecomputeMembership(): RecomputeMembershipLevel {
        if (this._isTargetDirty) {
            return RecomputeMembershipLevel.All;
        }
        return super.needRecomputeMembership;
    }

    public recompute(ns: Namespace): GroundValue {
        const result = super.recompute(ns);
        this._isTargetDirty = false;
        return result;
    }
}

// Computes one global aggregate over a whole table.
//
// Dirt:
// * pushes dirt whenever the aggregate changes
export class TableAggregateHandler<T, D = undefined> implements Handler {
    private readonly _slots: readonly IncomingSlot[];
    private _state: T | undefined;
    private _current: GroundValue;
    private readonly _aggregatedTableDirtyState: DirtyState;
    public readonly symbolicRepresentation: string;

    constructor(aggregatedTablePath: RootPath, private readonly _computation: TableAggregateComputation<T, D>) {
        const contextPaths = _computation.getContextPaths();
        const aggregatePaths = _computation.getAggregatePaths();

        this._slots = [
            { sourcePath: aggregatedTablePath, process: this.processDirt },
            ...makeSlotsForPaths([...contextPaths, ...aggregatePaths], false, this.processOtherDirt),
        ];

        this._aggregatedTableDirtyState = makeDirtyState(aggregatePaths, true);

        this.symbolicRepresentation = `aggregate ${getSymbolicRepresentationForPath(
            aggregatedTablePath
        )} computation: ${_computation.symbolicRepresentation}`;
    }

    public getSlots(): readonly IncomingSlot[] {
        return this._slots;
    }

    private makeMapperState(): MapperState<T> {
        return {
            get: () => this._state,
            set: v => (this._state = v),
            delete: () => (this._state = undefined),
        };
    }

    private makeAggregateProcessor(ns: Namespace): TableAggregateDataProvider<T, D> {
        let container: GroundValue;
        let derived: D | LoadingValue | undefined;
        const getAggregatedContainer = () => {
            if (container === undefined) {
                container = ns.get(this._slots[0].sourcePath, true);
            }
            return container;
        };

        const processor: TableAggregateDataProvider<T, D> = {
            getAggregatedTable: () => loadedDefinedMap(getAggregatedContainer(), asTable),
            forEachInAggregated: f => {
                const t = getAggregatedContainer();
                if (t === undefined || isLoadingValue(t)) return t;
                const maybeLoadingValue = forEachItem(t, f);
                if (maybeLoadingValue !== undefined) return maybeLoadingValue;
                return true;
            },
            getAggregatedAsArray: () => loadedDefinedMap(getAggregatedContainer(), containerAsArray),
            getContextRow: () => undefined,
            rootPathResolver: ns,
            deriveFromAggregatedTable: f => {
                if (derived === undefined) {
                    derived = loadedDefinedMap(processor.getAggregatedTable(), t => f(t));
                }
                return derived;
            },
            ...this.makeMapperState(),
        };
        return processor;
    }

    private makeProcessDirtProcessor(): ProcessDirtProcessor {
        return {
            setDirty: () => this._aggregatedTableDirtyState.add({ kind: "table", columns: true }),
        };
    }

    private processDirt = (d: Dirt): boolean => {
        return this._aggregatedTableDirtyState.add(d);
    };

    private processOtherDirt = (d: Dirt): boolean => {
        return this._computation.processOtherDirt(this.makeProcessDirtProcessor(), d);
    };

    public recompute(ns: Namespace): GroundValue {
        const proc = this.makeAggregateProcessor(ns);

        if (this.isDirty) {
            const newValue = this._computation.recompute(proc, this._aggregatedTableDirtyState, undefined);
            if (newValue !== this._current) {
                this._current = newValue;
                ns.pushDirt(this, { kind: "table", columns: true });
            }
        }

        this._aggregatedTableDirtyState.clear();
        return this._current;
    }

    public setDirty(): void {
        this._aggregatedTableDirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._aggregatedTableDirtyState.isDirty;
    }
}

// This does a global computation.
//
// Dirt:
// * pushes dirt whenever the computation result changes
export class ComputationHandler implements Handler {
    private readonly _slots: readonly IncomingSlot[];
    private _isDirty = dirtyFromTheStart;
    private _value: GroundValue;

    constructor(private readonly _computation: Computation) {
        this._slots = makeSlotsForPaths(_computation.getPaths(), true, this.processDirt);
    }

    public getSlots(): readonly IncomingSlot[] {
        return this._slots;
    }

    protected processDirt = (): boolean => {
        if (this._isDirty) return false;
        this._isDirty = true;
        return true;
    };

    public recompute(ns: Namespace): GroundValue {
        if (this._isDirty) {
            const oldValue = this._value;

            this._value = this._computation.compute(ns, undefined, makeSetAllDirty(ns, this));

            if (this._value !== oldValue) {
                ns.pushDirt(this, { kind: "table", columns: true });
            }

            this._isDirty = false;
        }

        return this._value;
    }

    public setDirty(): void {
        this._isDirty = true;
    }

    public get isDirty(): boolean {
        return this._isDirty;
    }

    public get symbolicRepresentation(): string {
        return `compute ${this._computation.symbolicRepresentation}`;
    }
}

// Adds a column that is produced by a computation.
//
// Dirt:
// * pushes dirt whenever a value in the computed column changes
// * it does not push dirt for changes in any of the other columns
export class ComputationComputedColumnHandler extends TableAndPathsHandler {
    private readonly _dirtyState: DirtyState;
    public readonly symbolicRepresentation: string;

    constructor(
        tablePath: RootPath,
        private readonly _columnName: string,
        private readonly _computation: Computation,
        private readonly _useThunks: boolean
    ) {
        // Root paths from the computation go into "other" dirt, and dirty the
        // whole computed columns.  Relative paths are columns in the table,
        // and only dirty the rows they're associated with.
        super(tablePath, _computation.getPaths());
        this._dirtyState = makeDirtyState(_computation.getPaths(), true);

        this.symbolicRepresentation = `compute-to-column ${_columnName} table: ${getSymbolicRepresentationForPath(
            tablePath
        )} computation: ${_computation.symbolicRepresentation}`;
    }

    protected processTableDirt(d: Dirt): boolean {
        return this._dirtyState.add(d);
    }

    protected processOtherDirt(_d: Dirt, _paths: readonly RootPath[]): boolean {
        // Not sure if we can be smarter here, definitely not without
        // knowledge about the specific computation we're doing.
        return this._dirtyState.setAllDirty();
    }

    private recomputeRow(ns: Namespace, row: Row, setAllDirty: () => void): boolean {
        if (this._useThunks && !ns.isStrictMode) {
            setThunkColumn(row, this._columnName, () => this._computation.compute(ns, row, setAllDirty));
        } else {
            const oldValue = getRowColumn(row, this._columnName);
            const newValue = this._computation.compute(ns, row, setAllDirty);
            if (oldValue === newValue) return false;
            row[this._columnName] = newValue;
        }

        this._dirtyState.addRowAndColumn(row.$rowID, this._columnName);
        return true;
    }

    public recompute(ns: Namespace): GroundValue {
        const table = this.getTable(ns);

        if (this.isDirty && !isLoadingValue(table)) {
            const setAllDirty = makeSetAllDirty(ns, this);

            const { dirtyRowIDs } = this._dirtyState;
            const outgoing = makeDirtyState(true);

            if (dirtyRowIDs === true) {
                tableForEach(table, true, row => {
                    if (this.recomputeRow(ns, row, setAllDirty)) {
                        outgoing.addRowAndColumn(row.$rowID, this._columnName);
                    }
                });
            } else {
                assert(dirtyRowIDs !== false);
                for (const rowID of dirtyRowIDs) {
                    const row = table.get(rowID);
                    // ##dirtForDeletedRows:
                    // If the row is undefined that probably means that it was
                    // deleted, which also means that we have to include it in
                    // the dirt because handlers that depend on us need to
                    // know that the value isn't there anymore.
                    // https://github.com/quicktype/glide/issues/11985
                    if (row === undefined || this.recomputeRow(ns, row, setAllDirty)) {
                        outgoing.addRowAndColumn(rowID, this._columnName);
                    }
                }
            }

            outgoing.pushAndClear(ns, this);
        }

        this._dirtyState.clear();

        return table;
    }

    public setDirty(): void {
        this._dirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._dirtyState.isDirty;
    }
}

// This maintains `MapperState`s for each row in a table.  Say we want to do a
// Single Relation, but instead of it being a global value, it needs to be a
// true computed column, so instead of needing a single `MapperState` we need
// one for each row in the table that hosts the
class TableMapperState<T> {
    private readonly _state = new Map<string, T>();

    public makeMapperState(rowID: string): MapperState<T> {
        return {
            get: () => this._state.get(rowID),
            set: (v: T) => this._state.set(rowID, v),
            delete: () => this._state.delete(rowID),
        };
    }

    public clear(): void {
        this._state.clear();
    }
}

// We use this for rollups, multi-lookups, and single values that are real
// computed columns, i.e. not global.
//
// Dirt:
// * pushes dirt whenever a value in the computed column changes
// * it does not push dirt for changes in any of the other columns
export class TableAggregateComputedColumnHandler<T, D> implements Handler {
    private readonly _slots: readonly IncomingSlot[];
    private readonly _state = new TableMapperState<T>();
    private readonly _contextDirtyState: DirtyState;
    private readonly _aggregatedDirtyState: DirtyState;
    public readonly symbolicRepresentation: string;

    constructor(
        // The table to which we're adding the computed column
        contextTablePath: RootPath,
        // The path to the multi-relation which we're aggregating over.  This
        // can be relative to the `contextTablePath` or a root path.
        private readonly _relationPath: Path,
        // The table containing the data that we're aggregating, i.e. the
        // target table of the above multi-relation.  If we're aggregating
        // over an array, this is `undefined`.
        aggregatedTablePath: RootPath | undefined,
        private readonly _computation: TableAggregateComputation<T, D>,
        private readonly _columnName: string,
        private readonly _useThunks: boolean
    ) {
        const contextPaths = _computation.getContextPaths();
        const aggregatePaths = _computation.getAggregatePaths();

        this._contextDirtyState = makeDirtyState([_relationPath, ...contextPaths], true);
        this._aggregatedDirtyState = makeDirtyState(aggregatePaths, true);

        const slots = [
            { sourcePath: contextTablePath, process: this.processContextDirt },
            ...makeSlotsForPaths([...contextPaths, ...aggregatePaths], false, this.processOtherDirt),
        ];
        if (aggregatedTablePath !== undefined) {
            slots.push({ sourcePath: aggregatedTablePath, process: this.processAggregatedDirt });
        }
        if (isRootPath(_relationPath)) {
            // The alternative to subscribing to `_relationPath` would be that
            // the caller combines it with `aggregatedTablePath`.
            slots.push({ sourcePath: _relationPath, process: this.processAggregatedDirt });
        }
        this._slots = slots;

        this.symbolicRepresentation = `aggregate-to-column ${_columnName} table: ${getSymbolicRepresentationForPath(
            contextTablePath
        )} relation: ${getSymbolicRepresentationForPath(_relationPath)} computation: ${
            _computation.symbolicRepresentation
        }`;
    }

    public getSlots(): readonly IncomingSlot[] {
        return this._slots;
    }

    private get contextTablePath(): RootPath {
        return this._slots[0].sourcePath;
    }

    private getAggregatedContainer(ns: RootPathResolver, row: Row): GroundValue {
        return getValueAt(ns, row, this._relationPath);
    }

    private makeAggregateProcessor(
        ns: Namespace,
        contextRow: Row,
        derivedRef: Ref<D | LoadingValue>
    ): TableAggregateDataProvider<T, D> {
        const getAggregatedContainer = () => this.getAggregatedContainer(ns, contextRow);
        const provider: TableAggregateDataProvider<T, D> = {
            getAggregatedTable: () => loadedDefinedMap(getAggregatedContainer(), asTable),
            forEachInAggregated: f => {
                const t = getAggregatedContainer();
                if (t === undefined || isLoadingValue(t)) return t;
                const maybeLoadingValue = forEachItem(t, f);
                if (maybeLoadingValue !== undefined) return maybeLoadingValue;
                return true;
            },
            getAggregatedAsArray: () => loadedDefinedMap(getAggregatedContainer(), containerAsArray),
            rootPathResolver: ns,
            getContextRow: () => contextRow,
            deriveFromAggregatedTable: f => {
                if (isRootPath(this._relationPath)) {
                    if (derivedRef.value === undefined) {
                        derivedRef.value = loadedDefinedMap(provider.getAggregatedTable(), t => f(t));
                    }
                    return derivedRef.value;
                } else {
                    return loadedDefinedMap(provider.getAggregatedTable(), t => f(t));
                }
            },
            ...this._state.makeMapperState(contextRow.$rowID),
        };
        return provider;
    }

    private processContextDirt = (d: Dirt): boolean => {
        return this._contextDirtyState.add(d);
    };

    private processAggregatedDirt = (d: Dirt): boolean => {
        return this._aggregatedDirtyState.add(d);
    };

    private processOtherDirt = (_d: Dirt, _paths: readonly RootPath[]): boolean => {
        return this._contextDirtyState.setAllDirty();
    };

    private recomputeForRow(
        ns: Namespace,
        row: Row,
        aggregatedDirtyState: DirtyState,
        contextDirtyState: DirtyState,
        derivedRef: Ref<D | LoadingValue>
    ): GroundValue {
        return this._computation.recompute(
            this.makeAggregateProcessor(ns, row, derivedRef),
            aggregatedDirtyState,
            contextDirtyState
        );
    }

    public recompute(ns: Namespace): GroundValue {
        const contextTableValue = ns.get(this.contextTablePath, true);
        const outgoing = makeDirtyState(true);

        let aggregatedDirtyState = this._aggregatedDirtyState;
        let contextDirtyState = this._contextDirtyState;
        if (this._useThunks) {
            aggregatedDirtyState = aggregatedDirtyState.clone();
            contextDirtyState = contextDirtyState.clone();
        }

        const derivedRef: Ref<D | LoadingValue> = {};

        if (this.isDirty && !isLoadingValue(contextTableValue)) {
            const contextTable = asTable(contextTableValue);
            tableForEach(contextTable, true, row => {
                // FIXME: If only the context table is dirty, we can avoid recomputing
                // if only some rows are dirty.
                if (this._useThunks && !ns.isStrictMode) {
                    setThunkColumn(row, this._columnName, () =>
                        this.recomputeForRow(ns, row, aggregatedDirtyState, contextDirtyState, derivedRef)
                    );
                } else {
                    const oldValue = getRowColumn(row, this._columnName);
                    const newValue = this.recomputeForRow(ns, row, aggregatedDirtyState, contextDirtyState, derivedRef);
                    if (oldValue === newValue) return;
                    row[this._columnName] = newValue;
                }
                outgoing.addRowAndColumn(row.$rowID, this._columnName);
            });
        }

        outgoing.pushAndClear(ns, this);
        this._contextDirtyState.clear();
        this._aggregatedDirtyState.clear();

        return contextTableValue;
    }

    public setDirty(): void {
        this._contextDirtyState.setAllDirty();
        this._aggregatedDirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._contextDirtyState.isDirty || this._aggregatedDirtyState.isDirty;
    }
}

// Dirt:
// * pushes dirt whenever the set of rows in of computed column changes
// * it does not push dirt for changes in any of the other columns
// * it does not push dirt for changes in the filtered table
export class FilterComputedColumnHandler implements Handler {
    private readonly _hostDirtyState: DirtyState;
    private readonly _filteredDirtyState: DirtyState;
    private readonly _slots: readonly IncomingSlot[];
    public readonly symbolicRepresentation: string;

    // `filteredTablePath` can be either a global path, or a key path into the
    // host table.  `filteredTableGlobalPath` must be the path to the whole
    // filtered table that will produce dirt on it.
    constructor(
        hostTablePath: RootPath,
        private readonly _filteredTablePath: Path,
        filteredTableGlobalPath: RootPath,
        private readonly _predicate: FilterPredicate,
        private readonly _columnName: string
    ) {
        this._hostDirtyState = makeDirtyState([_filteredTablePath, ..._predicate.getHostPaths()]);
        this._filteredDirtyState = makeDirtyState(_predicate.getFilteredPaths());

        this._slots = [
            { sourcePath: hostTablePath, process: d => this.processHostDirt(d) },
            { sourcePath: filteredTableGlobalPath, process: d => this.processFilteredDirt(d) },
            ...makeSlotsForPaths(_predicate.getFilteredPaths(), false, d => this.processOtherDirt(d)),
        ];

        this.symbolicRepresentation = `filter-to-column ${_columnName} table: ${getSymbolicRepresentationForPath(
            hostTablePath
        )} source: ${getSymbolicRepresentationForPath(_filteredTablePath)} by: ${_predicate.symbolicRepresentation}`;
    }

    public getSlots(): readonly IncomingSlot[] {
        return this._slots;
    }

    private get hostTablePath(): RootPath {
        return this._slots[0].sourcePath;
    }

    private processHostDirt(d: Dirt): boolean {
        return this._hostDirtyState.add(d);
    }

    private processFilteredDirt(d: Dirt): boolean {
        return this._filteredDirtyState.add(d);
    }

    private processOtherDirt(_d: Dirt): boolean {
        return this._hostDirtyState.setAllDirty();
    }

    public recompute(ns: Namespace): GroundValue {
        const hostTableValue = ns.get(this.hostTablePath, true);

        if (this.isDirty && !isLoadingValue(hostTableValue)) {
            const hostTable = asTable(hostTableValue);
            const setAllDirty = makeSetAllDirty(ns, this);

            const getUnfiltered = makeGetValueAt(ns, this._filteredTablePath);
            const computeForRow = (hostRow: Row): GroundValue | LoadingValue => {
                const unfilteredValue = getUnfiltered(hostRow);
                if (isLoadingValue(unfilteredValue) || unfilteredValue === undefined) return unfilteredValue;

                const unfiltered = asTable(unfilteredValue);
                if (unfiltered === undefined) return undefined;

                const result: Table = new Map();
                tableForEach(unfiltered, false, r => {
                    if (!this._predicate.isRowIncluded(ns, r, hostRow, setAllDirty)) return;
                    result.set(r.$rowID, r);
                });

                return result;
            };

            const { dirtyRowIDs } = this._hostDirtyState;
            const columnsToDirty = new Set([this._columnName]);

            if (dirtyRowIDs === true || this._filteredDirtyState.isDirty) {
                tableForEach(hostTable, true, r => {
                    const thunk = () => computeForRow(r);
                    if (ns.isStrictMode) {
                        r[this._columnName] = thunk();
                    } else {
                        setThunkColumn(r, this._columnName, thunk);
                    }
                });

                ns.pushDirt(this, { kind: "table", columns: columnsToDirty });
            } else {
                assert(dirtyRowIDs !== false);
                for (const rowID of dirtyRowIDs) {
                    const r = hostTable.get(rowID);
                    if (r === undefined) continue;
                    const thunk = () => computeForRow(r);
                    if (ns.isStrictMode) {
                        r[this._columnName] = thunk();
                    } else {
                        setThunkColumn(r, this._columnName, thunk);
                    }
                }

                for (const rowID of dirtyRowIDs) {
                    ns.pushDirt(this, { kind: "row", rowID, columns: columnsToDirty });
                }
            }

            this._hostDirtyState.clear();
        }

        return hostTableValue;
    }

    public setDirty(): void {
        this._hostDirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._hostDirtyState.isDirty || this._filteredDirtyState.isDirty;
    }
}

// If `sortKey` is `undefined` then we don't sort.
function sortRowsIntoTable(rows: readonly Row[], sortKey: RelativePath | undefined): Table {
    if (sortKey !== undefined) {
        rows = sortBy(Array.from(rows), r => follow(r, sortKey));
    }
    const table: Table = new Map();
    for (const r of rows) {
        table.set(r.$rowID, r);
    }
    return table;
}

// Dirt:
// * pushes dirt whenever a value in the computed column changes
// * it does not push dirt for changes in any of the other columns
abstract class RelationComputedColumnHandler implements Handler {
    private readonly _sourceSlot: IncomingSlot;
    private readonly _sourceColumnPath: RelativePath;
    private readonly _sourceDirtyState: DirtyState;
    public abstract readonly symbolicRepresentation: string;

    // FIXME: These indexes can also be kept just once for each table/key pair
    // across all handlers.  Here we keep them sorted, so maybe that wouldn't
    // be that easy.  But most sorts want to be by row index, so maybe that's
    // the default, or the only option the global index provides?
    private readonly _targetTablesForKey = new DefaultMap<string, Table>(() => new Map());

    constructor(
        sourceTableColumnPath: RootPath,
        protected readonly columnName: string,
        private readonly _sortKey: RelativePath | undefined,
        private readonly _sourceKeyIsArray: boolean
    ) {
        const source = deconstructTableColumnPath(sourceTableColumnPath);
        this._sourceSlot = { sourcePath: source.tablePath, process: this.processSourceDirt };
        this._sourceColumnPath = source.keyPath;
        this._sourceDirtyState = makeDirtyState(this._sourceColumnPath);
    }

    public getSlots(): readonly IncomingSlot[] {
        return [this._sourceSlot];
    }

    protected getTargetRowsForKeys(keys: Set<string>): Table {
        if (keys.size === 0) {
            return new Map();
        }
        if (keys.size === 1) {
            const [key] = Array.from(keys);
            return this._targetTablesForKey.get(key);
        }

        const rows: Row[] = [];
        const rowIDs = new Set<string>();
        for (const key of keys) {
            const table = this._targetTablesForKey.get(key);
            for (const [rowID, row] of table) {
                if (rowIDs.has(rowID)) continue;

                assert(row.$isVisible);
                rows.push(row);
                rowIDs.add(rowID);
            }
        }

        return sortRowsIntoTable(rows, this._sortKey);
    }

    protected abstract makeThunkForKeys(keys: Set<string>): () => GroundValue;

    private getSourceKeys(row: Row): Set<string> {
        return new Set(getRelationKeys(follow(row, this._sourceColumnPath), this._sourceKeyIsArray));
    }

    protected abstract getTargetKeys(ns: Namespace, row: Row): Set<string>;

    private processSourceDirt = (d: Dirt): boolean => {
        return this._sourceDirtyState.add(d);
    };

    private getSourceTable(ns: Namespace): Table | LoadingValue {
        const v = ns.get(this._sourceSlot.sourcePath, true);
        if (isLoadingValue(v)) return v;
        return asTable(v);
    }

    protected abstract getTargetTable(ns: Namespace): Table | LoadingValue;

    public recompute(ns: Namespace): GroundValue {
        const sourceTable = this.getSourceTable(ns);

        // FIXME: Only recompute dirty source rows if the target is not dirty.
        if (this.isDirty && !isLoadingValue(sourceTable)) {
            this._targetTablesForKey.clear();

            const targetTable = this.getTargetTable(ns);

            if (!isLoadingValue(targetTable)) {
                const targetRowsForKey = new DefaultMap<string, Row[]>(() => []);
                tableForEach(targetTable, false, row => {
                    const keys = this.getTargetKeys(ns, row);
                    for (const key of keys) {
                        targetRowsForKey.get(key).push(row);
                    }
                });

                for (const [key, rows] of targetRowsForKey) {
                    this._targetTablesForKey.set(key, sortRowsIntoTable(rows, this._sortKey));
                }

                tableForEach(sourceTable, true, row => {
                    const keys = this.getSourceKeys(row);
                    const thunk = this.makeThunkForKeys(keys);

                    if (ns.isStrictMode) {
                        row[this.columnName] = thunk();
                    } else {
                        setThunkColumn(row, this.columnName, thunk);
                    }
                });

                ns.pushDirt(this, { kind: "table", columns: new Set([this.columnName]) });
            }
        }

        this._sourceDirtyState.clear();

        return sourceTable;
    }

    public setDirty(): void {
        this._sourceDirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._sourceDirtyState.isDirty;
    }
}

abstract class RelationToColumnComputedColumnHandler extends RelationComputedColumnHandler {
    private readonly _targetColumnPath: RelativePath;
    private readonly _targetDirtyState: DirtyState;
    private readonly _targetSlot: IncomingSlot;

    constructor(
        sourceTableColumnPath: RootPath,
        targetTableColumnPath: RootPath,
        columnName: string,
        targetRowIndexDirtiesAll: boolean,
        sortKey: RelativePath | undefined,
        sourceKeyIsArray: boolean,
        private readonly _targetKeyIsArray: boolean
    ) {
        super(sourceTableColumnPath, columnName, sortKey, sourceKeyIsArray);

        const target = deconstructTableColumnPath(targetTableColumnPath);

        this._targetSlot = { sourcePath: target.tablePath, process: this.processTargetDirt };
        this._targetColumnPath = target.keyPath;
        const targetColumns = [this._targetColumnPath];
        if (targetRowIndexDirtiesAll) {
            targetColumns.push(makeKeyPath(rowIndexColumnName));
        }
        this._targetDirtyState = makeDirtyState(targetColumns);
    }

    public getSlots(): readonly IncomingSlot[] {
        return [...super.getSlots(), this._targetSlot];
    }

    private processTargetDirt = (d: Dirt): boolean => {
        return this._targetDirtyState.add(d);
    };

    protected getTargetKeys(_ns: Namespace, row: Row): Set<string> {
        return new Set(getRelationKeys(follow(row, this._targetColumnPath), this._targetKeyIsArray));
    }

    protected getTargetTable(ns: Namespace): Table | LoadingValue {
        const v = ns.get(this._targetSlot.sourcePath, true);
        if (isLoadingValue(v)) return v;
        return asTable(v);
    }

    public recompute(ns: Namespace): GroundValue {
        const result = super.recompute(ns);
        this._targetDirtyState.clear();
        return result;
    }

    public setDirty(): void {
        super.setDirty();
        this._targetDirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return super.isDirty || this._targetDirtyState.isDirty;
    }
}

abstract class RelationToGlobalComputedColumnHandler extends RelationComputedColumnHandler {
    private readonly _targetDirtyState: DirtyState;
    private readonly _targetTableSlot: IncomingSlot;
    private readonly _targetSlot: IncomingSlot;

    constructor(
        sourceTableColumnPath: RootPath,
        targetTablePath: RootPath,
        targetPath: RootPath,
        columnName: string,
        targetRowIndexDirtiesAll: boolean,
        sortKey: RelativePath | undefined,
        sourceKeyIsArray: boolean,
        private readonly _targetKeyIsArray: boolean
    ) {
        super(sourceTableColumnPath, columnName, sortKey, sourceKeyIsArray);

        this._targetTableSlot = { sourcePath: targetTablePath, process: this.processTargetTableDirt };

        const targetColumns = [makeKeyPath("$rowID")];
        if (targetRowIndexDirtiesAll) {
            targetColumns.push(makeKeyPath(rowIndexColumnName));
        }
        this._targetDirtyState = makeDirtyState(targetColumns);

        this._targetSlot = { sourcePath: targetPath, process: this.processTargetDirt };
    }

    public getSlots(): readonly IncomingSlot[] {
        return [...super.getSlots(), this._targetTableSlot, this._targetSlot];
    }

    private processTargetTableDirt = (d: Dirt): boolean => {
        return this._targetDirtyState.add(d);
    };

    private processTargetDirt = (): boolean => {
        return this._targetDirtyState.setAllDirty();
    };

    protected getTargetKeys(ns: Namespace): Set<string> {
        return new Set(getRelationKeys(ns.get(this._targetSlot.sourcePath), this._targetKeyIsArray));
    }

    protected getTargetTable(ns: Namespace): Table | LoadingValue {
        const v = ns.get(this._targetTableSlot.sourcePath, true);
        if (isLoadingValue(v)) return v;
        return asTable(v);
    }

    public recompute(ns: Namespace): GroundValue {
        const result = super.recompute(ns);
        this._targetDirtyState.clear();
        return result;
    }

    public setDirty(): void {
        super.setDirty();
        this._targetDirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return super.isDirty || this._targetDirtyState.isDirty;
    }
}

export class MultiRelationToColumnComputedColumnHandler extends RelationToColumnComputedColumnHandler {
    public readonly symbolicRepresentation: string;

    constructor(
        sourceTableColumnPath: RootPath,
        targetTableColumnPath: RootPath,
        columnName: string,
        sortKey: RelativePath | undefined,
        sourceKeyIsArray: boolean,
        targetKeyIsArray: boolean
    ) {
        super(
            sourceTableColumnPath,
            targetTableColumnPath,
            columnName,
            false,
            sortKey,
            sourceKeyIsArray,
            targetKeyIsArray
        );

        let symbolic = `multi-relation-to-column ${columnName} source: ${getSymbolicRepresentationForPath(
            sourceTableColumnPath
        )} target: ${getSymbolicRepresentationForPath(targetTableColumnPath)}`;
        if (sortKey !== undefined) {
            symbolic = `${symbolic} sortBy: ${getSymbolicRepresentationForPath(sortKey)}`;
        }
        this.symbolicRepresentation = symbolic;
    }

    private getMatchingTargetRowsForKeys(keys: Set<string>): Table {
        return this.getTargetRowsForKeys(keys);
    }

    protected makeThunkForKeys(keys: Set<string>): () => GroundValue {
        return () => this.getMatchingTargetRowsForKeys(keys);
    }
}

export class MultiRelationToGlobalComputedColumnHandler extends RelationToGlobalComputedColumnHandler {
    public readonly symbolicRepresentation: string;

    constructor(
        sourceTableColumnPath: RootPath,
        targetTablePath: RootPath,
        targetPath: RootPath,
        columnName: string,
        sortKey: RelativePath,
        sourceKeyIsArray: boolean,
        targetKeyIsArray: boolean
    ) {
        super(
            sourceTableColumnPath,
            targetTablePath,
            targetPath,
            columnName,
            false,
            sortKey,
            sourceKeyIsArray,
            targetKeyIsArray
        );

        this.symbolicRepresentation = `multi-relation-to-column ${columnName} source: ${getSymbolicRepresentationForPath(
            sourceTableColumnPath
        )} target: ${getSymbolicRepresentationForPath(targetPath)} sortBy: ${getSymbolicRepresentationForPath(
            sortKey
        )}`;
    }

    private getMatchingTargetRowsForKeys(keys: Set<string>): Table {
        return this.getTargetRowsForKeys(keys);
    }

    protected makeThunkForKeys(keys: Set<string>): () => GroundValue {
        return () => this.getMatchingTargetRowsForKeys(keys);
    }
}

function getRowIndex(row: Row): BaseRowIndex | undefined {
    const rowIndex = getRowColumn(row, rowIndexColumnName);
    if (rowIndex === undefined) return undefined;
    assert(isBaseRowIndex(rowIndex));
    return rowIndex;
}

function getFirstRow(rows: Table): [BaseRowIndex | undefined, Row] | undefined {
    let firstRow: Row | undefined;
    let firstRowIndex: BaseRowIndex | undefined;
    tableForEach(rows, false, row => {
        const rowIndex = getRowIndex(row);
        if (
            firstRow === undefined ||
            (rowIndex !== undefined && (firstRowIndex === undefined || rowIndex < firstRowIndex))
        ) {
            firstRow = row;
            firstRowIndex = rowIndex;
        }
    });
    if (firstRow === undefined) return undefined;
    return [firstRowIndex, firstRow];
}

// FIXME: This must get dirt on the row index column, as well as use that to
// invalidate.
export class SingleRelationToColumnComputedColumnHandler extends RelationToColumnComputedColumnHandler {
    public readonly symbolicRepresentation: string;

    constructor(
        sourceTableColumnPath: RootPath,
        targetTableColumnPath: RootPath,
        columnName: string,
        sourceKeyIsArray: boolean,
        targetKeyIsArray: boolean
    ) {
        super(
            sourceTableColumnPath,
            targetTableColumnPath,
            columnName,
            true,
            undefined,
            sourceKeyIsArray,
            targetKeyIsArray
        );

        this.symbolicRepresentation = `relation-to-column ${columnName} source: ${getSymbolicRepresentationForPath(
            sourceTableColumnPath
        )} target: ${getSymbolicRepresentationForPath(targetTableColumnPath)}`;
    }

    private getMatchingTargetRowForKeys(keys: Set<string>): [BaseRowIndex | undefined, Row] | undefined {
        return getFirstRow(this.getTargetRowsForKeys(keys));
    }

    protected makeThunkForKeys(keys: Set<string>): () => GroundValue {
        return () => this.getMatchingTargetRowForKeys(keys)?.[1];
    }
}

// FIXME: This must get dirt on the row index column, as well as use that to
// invalidate.
export class SingleRelationToGlobalComputedColumnHandler extends RelationToGlobalComputedColumnHandler {
    public readonly symbolicRepresentation: string;

    constructor(
        sourceTableColumnPath: RootPath,
        targetTablePath: RootPath,
        targetPath: RootPath,
        columnName: string,
        sourceKeyIsArray: boolean,
        targetKeyIsArray: boolean
    ) {
        super(
            sourceTableColumnPath,
            targetTablePath,
            targetPath,
            columnName,
            true,
            undefined,
            sourceKeyIsArray,
            targetKeyIsArray
        );

        this.symbolicRepresentation = `relation-to-column ${columnName} source: ${getSymbolicRepresentationForPath(
            sourceTableColumnPath
        )} target: ${getSymbolicRepresentationForPath(targetPath)}`;
    }

    private getMatchingTargetRowForKeys(keys: Set<string>): [BaseRowIndex | undefined, Row] | undefined {
        return getFirstRow(this.getTargetRowsForKeys(keys));
    }

    protected makeThunkForKeys(keys: Set<string>): () => GroundValue {
        return () => this.getMatchingTargetRowForKeys(keys)?.[1];
    }
}

let mockedNewComputationModelUUID: string | undefined;
export function mockNewComputationModelUUID(value: string | undefined) {
    mockedNewComputationModelUUID = value;
}

export class UUIDColumnHandler implements Handler {
    private readonly _dirtyState = makeDirtyState([makeKeyPath("$rowID")]);
    private readonly _slot: IncomingSlot;
    private readonly _uuidForRowID = new DefaultMap<string, string>(() => mockedNewComputationModelUUID ?? uuid());
    public readonly symbolicRepresentation: string;

    constructor(tablePath: RootPath, private readonly _columnName: string) {
        this._slot = { sourcePath: tablePath, process: this.processDirt };

        this.symbolicRepresentation = `uuid-to-column ${_columnName} table: ${getSymbolicRepresentationForPath(
            tablePath
        )}`;
    }

    public getSlots(): readonly IncomingSlot[] {
        return [this._slot];
    }

    private readonly processDirt = (d: Dirt): boolean => {
        return this._dirtyState.add(d);
    };

    public recompute(ns: Namespace): GroundValue {
        const tableValue = ns.get(this._slot.sourcePath);

        const { dirtyRowIDs } = this._dirtyState;
        if (dirtyRowIDs === false) {
            return tableValue;
        }

        if (!isLoadingValue(tableValue)) {
            const outgoing = makeDirtyState(true);
            const table = asTable(tableValue);

            if (dirtyRowIDs === true) {
                tableForEach(table, true, r => {
                    r[this._columnName] = this._uuidForRowID.get(r.$rowID);
                });
                outgoing.add({ kind: "table", columns: new Set([this._columnName]) });
            } else {
                for (const rowID of dirtyRowIDs) {
                    // We need ##dirtForDeletedRows.
                    outgoing.addRowAndColumn(rowID, this._columnName);

                    const r = table.get(rowID);
                    if (r === undefined) continue;
                    r[this._columnName] = this._uuidForRowID.get(rowID);
                }
            }

            outgoing.pushAndClear(ns, this);
        }

        this._dirtyState.clear();

        return tableValue;
    }

    public get isDirty(): boolean {
        return this._dirtyState.isDirty;
    }

    public setDirty(): void {
        this._dirtyState.setAllDirty();
    }
}

// Dirt from `path` will be passed through unmodified.
interface PassThroughCombineSpec {
    readonly kind: "pass-through";
    readonly path: RootPath;
}

// Any dirt from the table `tableColumnPath` dirties the whole column
// `columnToDirty`.
interface FullColumnCombineSpec {
    readonly kind: "full-column";
    readonly tablePath: RootPath;
    readonly columnToDirty: string;
}

// Any dirt from column `columnName` in table `tablePath` dirties the whole
// table.
interface FullTableFromColumnCombineSpec {
    readonly kind: "full-table-from-column";
    readonly tablePath: RootPath;
    readonly columnName: string;
}

// Any dirt from the table `tableColumnPath` dirties the whole table.
interface FullTableCombineSpec {
    readonly kind: "full-table";
    readonly tablePath: RootPath;
}

type CombineSpec =
    | PassThroughCombineSpec
    | FullColumnCombineSpec
    | FullTableFromColumnCombineSpec
    | FullTableCombineSpec;

// Dirt:
// * pushes whatever the specs say
export class CombineHandler implements Handler {
    private readonly _dirtyState = makeDirtyState(true);
    private readonly _slots: readonly IncomingSlot[];
    public readonly symbolicRepresentation: string;

    constructor(private readonly _resultPath: RootPath, specs: readonly CombineSpec[]) {
        const specsForKey = new DefaultMap<string, CombineSpec[]>(() => []);

        assert(isTopLevelPath(_resultPath));

        const symbolicParts: string[] = [`combine ${getSymbolicRepresentationForPath(_resultPath)}`];

        for (const spec of specs) {
            let key: string;
            if (spec.kind === "pass-through") {
                assert(isTopLevelPath(spec.path));
                key = spec.path.rest.key;

                symbolicParts.push(`pass-through: ${getSymbolicRepresentationForPath(spec.path)}`);
            } else if (
                spec.kind === "full-column" ||
                spec.kind === "full-table-from-column" ||
                spec.kind === "full-table"
            ) {
                key = spec.tablePath.rest.key;

                symbolicParts.push(`${spec.kind}: ${getSymbolicRepresentationForPath(spec.tablePath)}`);
            } else {
                assertNever(spec);
            }

            specsForKey.get(key).push(spec);
        }

        this._slots = Array.from(specsForKey).map(([k, s]) => ({
            sourcePath: makeRootPath(k),
            process: d => this.processDirt(d, s),
        }));

        this.symbolicRepresentation = symbolicParts.join(" ");
    }

    public getSlots(): readonly IncomingSlot[] {
        return this._slots;
    }

    private readonly processDirt = (d: Dirt, specs: readonly CombineSpec[]): boolean => {
        let didDirty = false;
        for (const spec of specs) {
            if (spec.kind === "pass-through") {
                if (this._dirtyState.add(d)) {
                    didDirty = true;
                }
            } else if (spec.kind === "full-column") {
                if (this._dirtyState.add({ kind: "table", columns: new Set([spec.columnToDirty]) })) {
                    didDirty = true;
                }
            } else if (spec.kind === "full-table-from-column") {
                if (d.columns === true || d.columns.has(spec.columnName)) {
                    if (this._dirtyState.add({ kind: "table", columns: true })) {
                        didDirty = true;
                    }
                }
            } else if (spec.kind === "full-table") {
                if (this._dirtyState.add({ kind: "table", columns: true })) {
                    didDirty = true;
                }
            } else {
                return assertNever(spec);
            }
        }
        return didDirty;
    };

    public recompute(ns: Namespace): GroundValue {
        this._dirtyState.pushAndClear(ns, this);
        return ns.get(this._resultPath, true);
    }

    public setDirty(): void {
        this._dirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._dirtyState.isDirty;
    }
}

let mockedNewComputationModelDate: GlideDateTime | undefined = undefined;
export function mockNewComputationModelDate(value: GlideDateTime | undefined) {
    mockedNewComputationModelDate = value;
}

// Dirt:
// * pushes dirt whenever the value changes
export class TimestampHandler implements Handler {
    private _updateInterval: ReturnType<typeof setInterval> | undefined;
    public readonly symbolicRepresentation = "timestamp";

    public getSlots(): readonly IncomingSlot[] {
        return [];
    }

    public recompute(_ns: Namespace): GroundValue {
        return mockedNewComputationModelDate ?? GlideDateTime.now();
    }

    public setDirty(): void {
        return;
    }

    public connect(ns: Namespace): void {
        assert(this._updateInterval === undefined);
        if (mockedNewComputationModelDate !== undefined) return;

        this._updateInterval = setInterval(() => {
            ns.pushDirt(this, { kind: "table", columns: true });
        }, 10000);
    }

    public disconnect(): void {
        if (mockedNewComputationModelDate !== undefined) {
            assert(this._updateInterval === undefined);
            return;
        }

        assert(this._updateInterval !== undefined);
        clearInterval(this._updateInterval);
        this._updateInterval = undefined;
    }

    public get isDirty(): boolean {
        return false;
    }
}

export interface UserSpecificRow extends Row {
    readonly $keyColumnName: string;
    readonly $keyColumnValue: PrimitiveValue;
}

// Dirt:
// * pushes dirt whenever the value in one of the user-specific columns
//   changes
// * does not push dirt for changes in other columns
// * does not push dirt if a user-specific row changes that's not bound to any
//   row.
export class JoinUserSpecificRowsHandler implements Handler {
    private readonly _slots: readonly IncomingSlot[];
    private readonly _tableDirtyState = makeDirtyState(true);
    private readonly _userSpecificDirtyState = makeDirtyState(true);
    private readonly _userSpecificColumnNames: Set<string>;
    private _hadUserSpecificRows = false;
    public readonly symbolicRepresentation: string;

    constructor(
        private readonly _tablePath: RootPath,
        private readonly _userSpecificRowsPath: RootPath,
        userSpecificColumnNames: Iterable<string>
    ) {
        this._slots = [
            {
                sourcePath: _tablePath,
                process: this.processTableDirt,
            },
            {
                sourcePath: _userSpecificRowsPath,
                process: this.processUserSpecificDirt,
            },
        ];
        this._userSpecificColumnNames = new Set(userSpecificColumnNames);

        this.symbolicRepresentation = `join-user-specific-rows table: ${getSymbolicRepresentationForPath(
            _tablePath
        )} user-specific-rows: ${getSymbolicRepresentationForPath(_userSpecificRowsPath)} columnNames: ${Array.from(
            userSpecificColumnNames
        ).join(",")}`;
    }

    public getSlots(): readonly IncomingSlot[] {
        return this._slots;
    }

    private processTableDirt = (d: Dirt): boolean => {
        return this._tableDirtyState.add(d);
    };

    private processUserSpecificDirt = (d: Dirt): boolean => {
        return this._userSpecificDirtyState.add(d);
    };

    public setDirty(): void {
        this._tableDirtyState.setAllDirty();
    }

    // FIXME: We're always recomputing everything.  How smart can/should we
    // be?  Keep the indexes?  Options:
    //
    // 1. Keep separate dirtiness on the rows, vs user rows.  If user rows
    //    have changed, go through them and to get the list of index columns.
    //    Then check whether the index columns have changed in the rows.  If
    //    they haven't, or only for a bunch of rows, we can update the indexes
    //    for those.  Then apply the changed user rows.  We have to make sure
    //    that we're not only adding, but also removing user-specific columns,
    //    of course.
    //
    // 2. Something simpler to start with: Keep a map from user-specific rows
    //    to table rows.  If anything's dirty, check whether the map is still
    //    correct.  If it isn't, rebuild everything.  But if it is, only those
    //    rows need to be updated.
    //
    // 3. This is what we should go with, until we have indexes: At first, do
    //    a full recalc and keep track which rows had user-specific data.  On
    //    every update, first make a list of which user rows go with which
    //    rows. This still requires building indexes (hopefully only one) and
    //    iterating over all user rows.  But then we need to only look at all
    //    the rows that previously had user-specific data, and those that now
    //    have some.  We update them, and only add dirt for the cases where
    //    something actually changed.  That will keep the outgoing dirt to a
    //    minimum.  If combined with option 2 above, we won't even need an
    //    index most of the time.
    //
    // FIXME: When the key column is the row ID, don't even build an index -
    // the table is that index!
    //
    // We can also keep a set of all the key columns used by the user-specific
    // rows.  If the user-specific rows don't change, and none of those key
    // columns change, no work needs to be done.
    public recompute(ns: Namespace): GroundValue {
        const tableValue = ns.get(this._tablePath);
        const userTableValue = ns.get(this._userSpecificRowsPath);

        if (!isLoadingValue(tableValue) && !isLoadingValue(userTableValue)) {
            const userTable = asTable(userTableValue);

            // This is a little more specific than the condition in `isDirty`.
            if (
                this._userSpecificDirtyState.isDirty ||
                (this._tableDirtyState.isDirty && (this._hadUserSpecificRows || userTable.size > 0))
            ) {
                const table = asTable(tableValue);

                // column name -> index
                // FIXME: This doesn't work with `Date`s.
                const indexes = new DefaultMap<string, Map<PrimitiveValue, Row>>(() => new Map());

                for (const u of userTable.values()) {
                    const s = u as UserSpecificRow;
                    indexes.get(checkString(s.$keyColumnName));
                    assert(isPrimitiveValue(s.$keyColumnValue));
                }

                for (const r of table.values()) {
                    for (const c of this._userSpecificColumnNames) {
                        r[c] = undefined;
                    }

                    for (const [c, index] of indexes) {
                        const v = getRowColumn(r, c);
                        if (v === undefined || isLoadingValue(v)) continue;

                        assert(isPrimitive(v));
                        index.set(v, r);
                    }
                }

                for (const u of userTable.values()) {
                    const s = u as UserSpecificRow;
                    const r = indexes.get(s.$keyColumnName).get(s.$keyColumnValue);
                    if (r === undefined) continue;
                    for (const c of this._userSpecificColumnNames) {
                        const v = getRowColumn(s, c);
                        if (v === undefined) continue;
                        r[c] = v;
                    }
                }

                ns.pushDirt(this, { kind: "table", columns: this._userSpecificColumnNames });

                this._hadUserSpecificRows = userTable.size > 0;
            }
        }

        this._tableDirtyState.clear();
        this._userSpecificDirtyState.clear();

        return tableValue;
    }

    public get isDirty(): boolean {
        return this._tableDirtyState.isDirty || this._userSpecificDirtyState.isDirty;
    }
}

// Dirt:
// * forwards all dirt from its input table
export class SortByHandler implements Handler {
    private readonly _slot: IncomingSlot;
    private readonly _columnName: string;
    private _value: Table | LoadingValue = new Map();
    private readonly _dirtyState = makeDirtyState(true);
    public readonly symbolicRepresentation: string;

    constructor(tableColumnPath: RootPath) {
        const { tablePath, keyPath } = deconstructTableColumnPath(tableColumnPath);
        this._slot = { sourcePath: tablePath, process: this.processDirt };
        assert(keyPath.rest === undefined);
        this._columnName = keyPath.key;

        this.symbolicRepresentation = `sort ${getSymbolicRepresentationForPath(tableColumnPath)}`;
    }

    public getSlots(): readonly IncomingSlot[] {
        return [this._slot];
    }

    private readonly processDirt = (d: Dirt): boolean => {
        return this._dirtyState.add(d);
    };

    public recompute(ns: Namespace): GroundValue {
        if (this._dirtyState.includesColumn(this._columnName)) {
            const tableValue = ns.get(this._slot.sourcePath, true);
            if (!isLoadingValue(tableValue)) {
                const table = asTable(tableValue);
                const sorted = sortBy(Array.from(table.values()), r => {
                    const v = getRowColumn(r, this._columnName);
                    if (isLoadingValue(v)) return undefined;
                    return asPrimitive(v);
                });
                const result: Table = new Map();
                for (const r of sorted) {
                    result.set(r.$rowID, r);
                }
                this._value = result;
            } else {
                this._value = tableValue;
            }
        }

        this._dirtyState.pushAndClear(ns, this);

        return this._value;
    }

    public setDirty(): void {
        this._dirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._dirtyState.isDirty;
    }
}

let mockedNewComputationModelCurrentLocation: LatLng | undefined;
export function mockNewComputationCurrentLocation(value: LatLng | undefined): void {
    mockedNewComputationModelCurrentLocation = value;
}

export class CurrentLocationHandler implements Handler, CurrentLocationListener {
    private _ns: Namespace | undefined;
    private _row: Row | undefined;
    public readonly symbolicRepresentation = "current-location";

    public getSlots(): readonly IncomingSlot[] {
        return [];
    }

    public recompute(): GroundValue {
        if (mockedNewComputationModelCurrentLocation !== undefined) {
            this._row = {
                $rowID: this._row?.$rowID ?? makeRowID(),
                $isVisible: false,
                latitude: mockedNewComputationModelCurrentLocation.lat,
                longitude: mockedNewComputationModelCurrentLocation.lng,
            };
        }
        return this._row;
    }

    private pushDirt(): void {
        defined(this._ns).pushDirt(this, { kind: "table", columns: true });
    }

    public updateLocation(latitude: number, longitude: number): void {
        if (mockedNewComputationModelCurrentLocation !== undefined) return;

        if (this._row === undefined) {
            this._row = {
                $rowID: makeRowID(),
                $isVisible: false,
                latitude,
                longitude,
            };
        } else {
            const oldLatitudeValue = getRowColumn(this._row, "latitude");
            const oldLongitudeValue = getRowColumn(this._row, "longitude");
            if (!isLoadingValue(oldLatitudeValue) && !isLoadingValue(oldLongitudeValue)) {
                const oldLatitude = defined(asMaybeNumber(oldLatitudeValue));
                const oldLongitude = defined(asMaybeNumber(oldLongitudeValue));
                if (latitude === oldLatitude && longitude === oldLongitude) return;
            }

            this._row.latitude = latitude;
            this._row.longitude = longitude;
        }

        this.pushDirt();
    }

    public locationError(): void {
        if (this._row === undefined) return;

        this._row = undefined;

        this.pushDirt();
    }

    public setDirty(): void {
        return;
    }

    public get isDirty(): boolean {
        return false;
    }

    public connect(ns: Namespace): void {
        assert(this._ns === undefined);
        this._ns = ns;
        listenToCurrentLocation(this);
    }

    public disconnect(ns: Namespace): void {
        assert(this._ns === ns);
        stopListeningToCurrentLocation(this);
        this._ns = undefined;
    }
}

interface AsyncState {
    value: GroundValue;
    // Boolean says computation is under way, and `true` means a follow-up is
    // needed.
    state: boolean | undefined;
}

export class AsyncComputationHandler implements Handler {
    private readonly _slots: readonly IncomingSlot[];
    private _isDirty = dirtyFromTheStart;
    private readonly _state: AsyncState = { value: makeLoadingValue(), state: undefined };

    constructor(private readonly _computation: AsyncComputation) {
        this._slots = makeSlotsForPaths(_computation.getPaths(), true, this.processDirt);
    }

    public getSlots(): readonly IncomingSlot[] {
        return this._slots;
    }

    protected processDirt = (): boolean => {
        if (this._isDirty) return false;
        this._isDirty = true;
        return true;
    };

    private async doRecompute(ns: Namespace): Promise<void> {
        const state = this._state;
        state.state = false;

        const promiseOrResult = this._computation.compute(ns, undefined);
        if (promiseOrResult instanceof Promise) {
            const newValue = await promiseOrResult;
            assert(state.state !== undefined);

            if (!ns.hasHandler(this)) return;

            const needsFollowUp = state.state;
            state.state = undefined;

            if (newValue !== state.value) {
                state.value = newValue;
                ns.pushDirt(this, { kind: "table", columns: true });
            }

            if (needsFollowUp) {
                // ##asyncComputationFollowUp:
                // TODO: It's a bit silly that we're waiting for the previous
                // computation to finish, then don't use the result, and start
                // a new computation.
                this.startComputation(ns);
            }
        } else {
            assert(state.state === false);
            state.state = undefined;

            if (promiseOrResult === state.value) return;

            state.value = promiseOrResult;
        }
    }

    private startComputation(ns: Namespace): void {
        assert(this._state.state === undefined);
        this._state.value = makeLoadingValue();
        dontAwait(this.doRecompute(ns));
    }

    public recompute(ns: Namespace): GroundValue {
        if (this._isDirty) {
            if (this._state.state !== undefined) {
                this._state.state = true;
            } else {
                this.startComputation(ns);
            }
            ns.pushDirt(this, { kind: "table", columns: true });
            this._isDirty = false;
        }

        return this._state.value;
    }

    public setDirty(): void {
        this._isDirty = true;
    }

    public get isDirty(): boolean {
        return this._isDirty;
    }

    public get symbolicRepresentation(): string {
        return `async-computation computation: ${this._computation.symbolicRepresentation}`;
    }
}

export class AsyncComputationComputedColumnHandler implements Handler {
    private readonly _slots: readonly IncomingSlot[];
    private readonly _dirtyState: DirtyState;
    private readonly _recomputedRowIDs = new Set<string>();
    private readonly _stateForRowID = new DefaultMap<string, AsyncState>(() => ({
        value: makeLoadingValue(),
        state: undefined,
    }));
    public readonly symbolicRepresentation: string;

    constructor(
        tablePath: RootPath,
        private readonly _columnName: string,
        private readonly _computation: AsyncComputation
    ) {
        const computationPaths = _computation.getPaths();

        const tableSlot: IncomingSlot = { sourcePath: tablePath, process: this.processTableDirt };
        const otherSlots = makeSlotsForPaths(computationPaths, false, this.processOtherDirt);
        this._slots = [tableSlot, ...otherSlots];

        this._dirtyState = makeDirtyState(computationPaths);

        this.symbolicRepresentation = `async-computation-to-column ${_columnName} table: ${getSymbolicRepresentationForPath(
            tablePath
        )} computation: ${_computation.symbolicRepresentation}`;
    }

    public getSlots(): readonly IncomingSlot[] {
        return this._slots;
    }

    private readonly processTableDirt = (d: Dirt): boolean => {
        return this._dirtyState.add(d);
    };

    private readonly processOtherDirt = (): boolean => {
        return this._dirtyState.setAllDirty();
    };

    private async doRecompute(ns: Namespace, row: Row): Promise<void> {
        const state = this._stateForRowID.get(row.$rowID);
        state.state = false;

        const promiseOrResult = this._computation.compute(ns, row);
        if (promiseOrResult instanceof Promise) {
            const newValue = await promiseOrResult;
            assert(state.state !== undefined);

            if (!ns.hasHandler(this)) return;

            const needsFollowUp = state.state;
            state.state = undefined;

            if (newValue !== state.value) {
                state.value = newValue;
                this._recomputedRowIDs.add(row.$rowID);
                ns.handlerWasDirtied(this);
            }

            if (needsFollowUp) {
                // We're doing ##asyncComputationFollowUp here, too.
                this.startComputation(ns, row);
            }
        } else {
            assert(state.state === false);
            state.state = undefined;

            if (promiseOrResult === state.value) return;

            state.value = promiseOrResult;
        }
    }

    private startComputation(ns: Namespace, row: Row): void {
        const state = this._stateForRowID.get(row.$rowID);
        assert(state.state === undefined);
        state.value = makeLoadingValue();
        dontAwait(this.doRecompute(ns, row));
    }

    private recomputeRow(ns: Namespace, row: Row): void {
        const thunk = () => {
            const state = this._stateForRowID.get(row.$rowID);
            if (state.state !== undefined) {
                state.state = true;
            } else {
                this.startComputation(ns, row);
            }
            return state.value;
        };

        if (ns.isStrictMode) {
            row[this._columnName] = thunk();
        } else {
            setThunkColumn(row, this._columnName, thunk);
        }
    }

    public recompute(ns: Namespace): GroundValue {
        const tableValue = ns.get(this._slots[0].sourcePath);

        if (this.isDirty && !isLoadingValue(tableValue)) {
            const table = asTable(tableValue);
            const { dirtyRowIDs } = this._dirtyState;
            const outgoing = makeDirtyState(true);

            if (dirtyRowIDs === true) {
                tableForEach(table, true, row => {
                    this.recomputeRow(ns, row);
                    outgoing.addRowAndColumn(row.$rowID, this._columnName);
                });
            } else {
                assert(dirtyRowIDs !== false || this._recomputedRowIDs.size > 0);

                for (const rowID of this._recomputedRowIDs) {
                    assert(this._stateForRowID.has(rowID));

                    if (dirtyRowIDs !== false && dirtyRowIDs.has(rowID)) continue;

                    // We need ##dirtForDeletedRows.
                    outgoing.addRowAndColumn(rowID, this._columnName);

                    const row = table.get(rowID);
                    if (row === undefined) continue;

                    row[this._columnName] = this._stateForRowID.get(rowID).value;
                }

                if (dirtyRowIDs !== false) {
                    for (const rowID of dirtyRowIDs) {
                        // We need ##dirtForDeletedRows.
                        outgoing.addRowAndColumn(rowID, this._columnName);

                        const row = table.get(rowID);
                        if (row === undefined) continue;

                        this.recomputeRow(ns, row);
                    }
                }
            }

            this._recomputedRowIDs.clear();
            outgoing.pushAndClear(ns, this);
        }

        this._dirtyState.clear();

        return tableValue;
    }

    public setDirty(): void {
        this._dirtyState.setAllDirty();
    }

    public get isDirty(): boolean {
        return this._dirtyState.isDirty || this._recomputedRowIDs.size > 0;
    }
}
